import React, { useState, useEffect } from 'react';
import './BootScreen.css';

function BootScreen({ onBootComplete }) {
  const [bootStage, setBootStage] = useState('post'); // 'post', 'kernel', 'fullscreen-halt', 'mobile-warning', 'complete'
  const [postMessages, setPostMessages] = useState([]);
  const [kernelMessages, setKernelMessages] = useState([]);
  const [isMobile, setIsMobile] = useState(false);
  const [showGlitch, setShowGlitch] = useState(false);

  // POST (Power-On Self Test) sequence
  const postSequence = [
    'BIOS Version 2.41.12 - Diptanshu Mainframe',
    'CPU: x86_64 @ 3.2GHz',
    'Memory: 32GB OK',
    'Detecting Primary Master... [ OK ]',
    'Booting from Hard Disk...'
  ];

  // Kernel boot sequence (will be dynamic based on mobile detection)
  const getKernelSequence = (isMobileDevice) => [
    '[    0.000000] Linux version 5.10.0-diptanshu (root@diptanshu)',
    '[    0.145221] ACPI: Core revision 20200528',
    '[    0.221415] [SECURITY] Conducting environment scan...',
    '[    0.451221] > DETECTING HARDWARE MONITOR...',
    `[    0.551221] > DEVICE IDENTIFIED: ${isMobileDevice ? 'MOBILE CLIENT [ WARNING ]' : 'DESKTOP CLIENT [ OK ]'}`,
    ...(isMobileDevice ? [] : [
      '[    0.621415] [SECURITY] Classified files detected in /home/visitor...',
      '[    0.881241] MOUNTING ROOT FILESYSTEM...',
      '[ OK ] Started Network Manager.',
      '[ OK ] Reached target Graphical Interface.'
    ])
  ];

  // Detect mobile device on mount
  useEffect(() => {
    const checkMobile = () => {
      const userAgent = navigator.userAgent || navigator.vendor || window.opera;
      const mobileRegex = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i;
      const isMobileDevice = mobileRegex.test(userAgent) || window.innerWidth < 768;
      setIsMobile(isMobileDevice);
    };

    checkMobile();
  }, []);

  // Stage 1: POST sequence
  useEffect(() => {
    if (bootStage !== 'post') return;

    let messageIndex = 0;
    const postInterval = setInterval(() => {
      if (messageIndex < postSequence.length) {
        setPostMessages(prev => [...prev, postSequence[messageIndex]]);
        messageIndex++;
      } else {
        clearInterval(postInterval);
        // Move to kernel stage after POST
        setTimeout(() => {
          setBootStage('kernel');
        }, 500);
      }
    }, 300);

    return () => clearInterval(postInterval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bootStage]);

  // Stage 2: Kernel sequence
  useEffect(() => {
    if (bootStage !== 'kernel') return;

    const kernelSeq = getKernelSequence(isMobile);
    let messageIndex = 0;

    const kernelInterval = setInterval(() => {
      if (messageIndex < kernelSeq.length) {
        setKernelMessages(prev => [...prev, kernelSeq[messageIndex]]);
        messageIndex++;
      } else {
        clearInterval(kernelInterval);
        // After kernel, show mobile warning or fullscreen prompt
        setTimeout(() => {
          if (isMobile) {
            setBootStage('mobile-warning');
          } else {
            // Glitch effect before fullscreen prompt
            setShowGlitch(true);
            setTimeout(() => {
              setShowGlitch(false);
              setBootStage('fullscreen-halt');
            }, 300);
          }
        }, 500);
      }
    }, 150); // Faster scrolling for kernel

    return () => clearInterval(kernelInterval);
  }, [bootStage, isMobile]);

  // Keyboard event listener for Enter and Escape keys
  useEffect(() => {
    if (bootStage !== 'fullscreen-halt') return;

    const handleKeyPress = (e) => {
      if (e.key === 'Enter') {
        requestFullscreen();
      } else if (e.key === 'Escape') {
        skipFullscreen();
      }
    };

    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bootStage]);

  // Handle fullscreen request
  const requestFullscreen = () => {
    const elem = document.documentElement;
    
    // Must call fullscreen API synchronously to preserve user gesture
    try {
      if (elem.requestFullscreen) {
        elem.requestFullscreen();
      } else if (elem.webkitRequestFullscreen) {
        elem.webkitRequestFullscreen();
      } else if (elem.mozRequestFullScreen) {
        elem.mozRequestFullScreen();
      } else if (elem.msRequestFullscreen) {
        elem.msRequestFullscreen();
      }
    } catch (err) {
      console.log('Fullscreen request failed:', err);
    }
    
    // Complete boot regardless of fullscreen success
    setBootStage('complete');
    setTimeout(() => onBootComplete(), 300);
  };

  // Skip to desktop
  const skipFullscreen = () => {
    setBootStage('complete');
    setTimeout(() => onBootComplete(), 300);
  };

  return (
    <div className={`boot-screen ${showGlitch ? 'glitch' : ''}`}>
      <div className="boot-terminal">
        
        {/* Stage 1: POST */}
        {bootStage === 'post' && (
          <div className="post-stage">
            {postMessages.map((msg, index) => (
              <div key={index} className="post-line">
                {msg}
              </div>
            ))}
            <div className="cursor">_</div>
          </div>
        )}

        {/* Stage 2: Kernel Load */}
        {(bootStage === 'kernel' || bootStage === 'fullscreen-halt') && (
          <div className="kernel-stage">
            {kernelMessages.map((msg, index) => (
              <div key={index} className="kernel-line">
                {msg}
              </div>
            ))}
          </div>
        )}

        {/* Stage 3: Fullscreen Halt Prompt */}
        {bootStage === 'fullscreen-halt' && (
          <div className="fullscreen-halt">
            <div className="halt-warning">[ SYSTEM WARNING ]</div>
            <div className="halt-message">
              Graphical Interface requires FULLSCREEN MODE
              <br />
              to simulate an isolated environment.
            </div>
            <div className="halt-prompt">
              <span className="prompt-text">{'>'} PRESS [ENTER] TO INITIALIZE DISPLAY DRIVER...</span>
              <span className="cursor-blink">_</span>
            </div>
            <div className="halt-actions">
              <button className="enter-key" onClick={requestFullscreen}>
                ENTER ⏎
              </button>
              <button className="skip-key" onClick={skipFullscreen}>
                ESC (Skip)
              </button>
            </div>
          </div>
        )}

        {/* Mobile Warning */}
        {bootStage === 'mobile-warning' && (
          <div className="mobile-warning-terminal">
            <div className="kernel-line error">[    0.621411] [ ERROR ] MOBILE DEVICE DETECTED</div>
            <div className="kernel-line error">[    0.651221] [ ERROR ] INCOMPATIBLE HARDWARE</div>
            <div className="kernel-line">[    0.721415] SYSTEM HALT</div>
            <div className="halt-separator">━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━</div>
            <div className="mobile-warning">
              <div className="warning-icon">📱</div>
              <h2 className="warning-title">Desktop Experience Required</h2>
              <p className="warning-description">
                This system is optimized for <strong>desktop and laptop</strong> viewing.
                <br />
                For the best experience, please visit on a larger screen.
              </p>
              
              <div className="mobile-contact">
                <div className="contact-label">Quick Contact:</div>
                <a href="mailto:dhawandiptanshu.work@gmail.com" className="mobile-email">
                  📧 dhawandiptanshu.work@gmail.com
                </a>
                <a href="https://www.linkedin.com/in/diptanshudhawan" target="_blank" rel="noopener noreferrer" className="mobile-linkedin">
                  💼 LinkedIn Profile
                </a>
                <a href="https://github.com/DiptanshuDhawan" target="_blank" rel="noopener noreferrer" className="mobile-github">
                  💻 GitHub Profile
                </a>
              </div>

              <button className="btn-continue-mobile" onClick={skipFullscreen}>
                Attempt Boot Anyway (Not Recommended)
              </button>
            </div>
          </div>
        )}

        {/* Boot Complete */}
        {bootStage === 'complete' && (
          <div className="boot-complete">
            <div className="complete-text">SYSTEM READY ✓</div>
          </div>
        )}

      </div>
    </div>
  );
}

export default BootScreen;
